/* eslint-disable no-else-return */
import {
  FlexBox,
  Modal,
  ModalBody,
  ModalHead,
  Page,
  Text,
} from '@dixa/lib-shared-habla';
import { electron } from 'lib-shared-electron';
import moment from 'moment-timezone';
import { useCallback, useEffect, useState } from 'react';

import styles from './settingsAbout.view.module.scss';
import licenses from './utils/json/license.json';
import { getLicense } from './utils/licenseTexts';

type License = {
  name: string;
  licenses: string;
  repository: string;
  publisher?: string;
  url?: string;
};

export const SettingsAboutView = (): JSX.Element => {
  const [activeLicense, setActiveLicense] = useState<License | null>(null);
  const [appVersion, setAppVersion] = useState<string>();

  useEffect(() => {
    if (!electron.isElectron()) {
      return;
    }

    electron.getVersion().then((version) => setAppVersion(version));
  }, []);

  const renderLicenseText = useCallback((license: License): string | null => {
    const publisher = license.publisher ?? '';

    if (license.licenses.indexOf('MIT') > -1) {
      return getLicense('MIT', publisher);
    }
    if (license.licenses.indexOf('ISC') > -1) {
      return getLicense('ISC', publisher);
    }
    if (license.licenses.indexOf('CC0-1.0') > -1) {
      return getLicense('CC0-1.0', publisher);
    }
    if (license.licenses === 'BSD-3-Clause') {
      return getLicense('BSD', publisher);
    }
    if (
      license.licenses === 'BSD-2-Clause' ||
      license.licenses.indexOf('BSD') > -1
    ) {
      return getLicense('BSD2', publisher);
    }
    if (
      license.licenses.indexOf('Apache-2.0') > -1 ||
      license.licenses.indexOf('Apache License, Version 2.0') > -1 ||
      license.licenses.indexOf('Apache') > -1
    ) {
      return getLicense('Apache-2.0', publisher);
    }
    if (license.licenses.indexOf('CC-BY-4.0') > -1) {
      return getLicense('CC-BY-4.0', publisher);
    }
    return null;
  }, []);

  return (
    <Page>
      <Page.Head title="About" />

      <Page.Body>
        <Page.Section
          title={`Dixa™ (build ${VERSION})${
            appVersion ? ` - (app version ${appVersion})` : ''
          }`}
        >
          <Text.Paragraph variant="2" subdued>
            <em>
              {' '}
              © 2015-{moment(TIMESTAMP.trim()).utc().format('Y')} Dixa™ All
              rights reserved.
            </em>
          </Text.Paragraph>

          <Text.Paragraph variant="2" subdued>
            The Dixa name, associated trademarks and logos are trademarks of
            Dixa or related entities.
            <br />
            The software platform and this app are protected by international
            copyright laws and international treaties.
            <br />
            Unauthorized reproduction or distribution of this software and app,
            or any portion thereof, is illegal and will be prosecuted.
          </Text.Paragraph>
        </Page.Section>

        <Page.Section title="Acknowledgements">
          <FlexBox
            flexDirection="column"
            padding="400"
            gap="200"
            background="neutral.100"
            borderRadius="100"
            borderColor="neutral.200"
            borderWidth="100"
          >
            {licenses.map((license: License, index) => (
              <Text.Paragraph
                variant="2"
                subdued
                key={index}
                className={styles.license}
              >
                {license.name}

                <Text.ExternalLink
                  href="#"
                  onClick={() => setActiveLicense(license)}
                >
                  License {license.licenses}
                </Text.ExternalLink>
              </Text.Paragraph>
            ))}
          </FlexBox>
        </Page.Section>

        {activeLicense && (
          <Modal
            isOpen
            allowClickOutside
            className={styles.modal}
            onClose={() => setActiveLicense(null)}
          >
            <ModalHead title="License" onClose={() => setActiveLicense(null)} />

            <ModalBody>
              <pre>{renderLicenseText(activeLicense)}</pre>
            </ModalBody>
          </Modal>
        )}
      </Page.Body>
    </Page>
  );
};
